import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  MagnifyingGlass, CheckCircle, ChatCircleText, ShieldCheck,
  UserCircle, Briefcase, Wallet, Chats, Lightning, Heartbeat,
  UsersFour, Kanban, Star, Tag,
} from '@phosphor-icons/react';

const CLIENT_STEPS = [
  { icon: MagnifyingGlass, title: 'Cerca l\'assistenza',        body: 'Scegli la categoria (Anziani, Bambini, Riabilitazione…) e i bisogni specifici dai 140+ disponibili.' },
  { icon: UsersFour,       title: 'Ricevi 5 pro suggeriti',     body: 'La nostra AI seleziona i migliori professionisti nella tua zona. Puoi aggiungerne altri manualmente.' },
  { icon: Chats,           title: 'Chatta e concorda',          body: 'Chat integrata per ogni prenotazione: coordini orari, ricette e dettagli senza uscire dalla piattaforma.' },
  { icon: Star,            title: 'Valuta e conserva',          body: 'A fine servizio lasci una recensione. La tua cartella assistenziale è sempre disponibile per te.' },
];

const PRO_STEPS = [
  { icon: UserCircle,   title: 'Registrati in 2 step',    body: 'Account + profilo professionale (professione, città, tariffa). L\'admin approva entro 24h.' },
  { icon: Tag,          title: 'Promozioni dedicate',     body: 'Pubblica sconti su una prestazione per un numero limitato di clienti: i clienti della tua zona le vedono subito.' },
  { icon: Lightning,    title: 'Ricevi lead in tempo reale', body: 'Notifica in-app + email non appena un cliente ti seleziona. Le richieste urgenti hanno badge in evidenza.' },
  { icon: Briefcase,    title: 'Sblocca la visibilità PRO', body: 'Con l\'abbonamento pro (da €29/mese) appari nei primi risultati e sblocchi analytics avanzate.' },
];

const ADMIN_STEPS = [
  { icon: Kanban,       title: 'Kanban CRM completo',     body: 'Trascina le richieste tra colonne (nuova → assegnata → in corso → completata) con filtri regione/provincia/distanza.' },
  { icon: UsersFour,    title: 'AI Matching on demand',   body: 'Un clic e l\'AI suggerisce i 5 pro migliori per una richiesta specifica, ordinati per rilevanza.' },
  { icon: Heartbeat,    title: 'Cartelle assistenziali',  body: 'Anamnesi, note visite e allegati di ogni paziente accessibili in ottica clinica per la continuità della cura.' },
  { icon: ShieldCheck,  title: 'Cronologia e audit trail',body: 'Tutte le azioni sono tracciate: chi ha modificato cosa, quando, e da quale IP.' },
];

const StepCard = ({ icon: Icon, title, body, i }) => (
  <Card className="neumorphism rounded-2xl p-6 relative overflow-hidden" data-testid={`hiw-step-${i}`}>
    <div className="absolute top-2 right-4 text-[64px] font-bold text-primary/5 leading-none select-none">
      {i}
    </div>
    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
      <Icon size={24} className="text-primary" weight="duotone" />
    </div>
    <h3 className="font-semibold text-lg mb-2">{title}</h3>
    <p className="text-sm text-foreground/70 leading-relaxed">{body}</p>
  </Card>
);

const Section = ({ title, subtitle, badge, steps, cta, keyPrefix }) => (
  <section className="mb-16" data-testid={`hiw-section-${keyPrefix}`}>
    <div className="flex items-center gap-2 mb-2">
      <Badge className="bg-primary/10 text-primary">{badge}</Badge>
    </div>
    <h2 className="font-heading font-semibold text-3xl mb-2">{title}</h2>
    <p className="text-foreground/70 mb-6 max-w-2xl">{subtitle}</p>
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {steps.map((s, i) => (
        <StepCard key={i} i={i + 1} icon={s.icon} title={s.title} body={s.body} />
      ))}
    </div>
    {cta}
  </section>
);

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-12" data-testid="how-it-works-page">
        {/* Hero */}
        <div className="text-center mb-16">
          <Badge className="bg-primary/10 text-primary mb-3">Come Funziona</Badge>
          <h1 className="font-heading font-semibold text-4xl sm:text-5xl mb-4">
            Assistenza sanitaria, semplificata.
          </h1>
          <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
            VivaLink Italia connette famiglie, professionisti e centri assistenza in un unico flusso. 
            Scopri come funziona per te.
          </p>
        </div>

        {/* Client */}
        <Section
          badge="Per Clienti"
          keyPrefix="client"
          title="Trova assistenza in 4 passaggi"
          subtitle="Dalla ricerca alla recensione, tutto in pochi minuti. Niente telefonate, niente attese: 5 professionisti giusti ti contattano loro."
          steps={CLIENT_STEPS}
          cta={
            <div className="flex flex-wrap gap-3">
              <Link to="/request"><Button size="lg" className="rounded-full" data-testid="hiw-cta-request">Richiedi assistenza ora</Button></Link>
              <Link to="/prestazioni"><Button size="lg" variant="outline" className="rounded-full" data-testid="hiw-cta-services">Vedi tutte le prestazioni</Button></Link>
            </div>
          }
        />

        {/* Pro */}
        <Section
          badge="Per Professionisti"
          keyPrefix="pro"
          title="Guadagna con la tua professione"
          subtitle="Registrati gratis, ricevi le richieste della tua zona e pubblica promozioni sulle tue prestazioni. La visibilità PRO ti mette in cima ai risultati."
          steps={PRO_STEPS}
          cta={
            <div className="flex flex-wrap gap-3">
              <Link to="/register-professional"><Button size="lg" className="rounded-full" data-testid="hiw-cta-register-pro">Diventa professionista</Button></Link>
              <Link to="/premium"><Button size="lg" variant="outline" className="rounded-full" data-testid="hiw-cta-premium">Scopri i piani PRO</Button></Link>
            </div>
          }
        />

        {/* Admin */}
        <Section
          badge="Per Team Interno"
          keyPrefix="admin"
          title="Uno strumento potente per l'operatività"
          subtitle="Il team VivaLink opera con un CRM completo: Kanban drag-and-drop, AI matching, cartelle cliniche e audit trail integrato."
          steps={ADMIN_STEPS}
          cta={null}
        />

        {/* Trust bar */}
        <Card className="neumorphism rounded-2xl p-8 mt-10 text-center bg-gradient-to-br from-primary/5 via-transparent to-accent/5" data-testid="hiw-trust-bar">
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="font-heading font-bold text-3xl text-primary">140+</div>
              <div className="text-sm text-foreground/70">Bisogni assistenziali censiti</div>
            </div>
            <div>
              <div className="font-heading font-bold text-3xl text-primary">18</div>
              <div className="text-sm text-foreground/70">Professioni sanitarie coperte</div>
            </div>
            <div>
              <div className="font-heading font-bold text-3xl text-primary">Promo</div>
              <div className="text-sm text-foreground/70">Sconti dedicati pubblicabili dai professionisti</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HowItWorksPage;
